import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PlusCircle, HelpCircle, Map, Globe, ChevronDown } from 'lucide-react';
import axios from 'axios';
import StatsCounter from '@/components/StatsCounter';
import PageMeta from '@/components/PageMeta';
import REGIONS from '@/data/regions.json';
import COUNTRIES from '@/data/countries.json';
import { API } from '@/components/map/MapHelpers';
import useInferredCountry from '@/hooks/useInferredCountry';

// Region display order
const REGION_ORDER = ['oceania', 'asia', 'europe', 'americas', 'africa'];

const GlobalApproachSection = () => {
  const [countryCounts, setCountryCounts] = useState([]);
  const [expandedRegion, setExpandedRegion] = useState(null);

  useEffect(() => {
    axios.get(`${API}/regions/country-counts`)
      .then(res => setCountryCounts(res.data || []))
      .catch(() => setCountryCounts([]));
  }, []);

  // Build a lookup: region_slug -> {total, allCountries, name}
  const regionStats = REGION_ORDER.reduce((acc, slug) => {
    const regionDef = REGIONS[slug];
    if (!regionDef) return acc;

    const memberCodes = new Set(regionDef.countries.map(c => c.toUpperCase()));
    const members = countryCounts
      .filter(c => c.country_code && memberCodes.has(c.country_code.toUpperCase()))
      .sort((a, b) => (b.place_count || 0) - (a.place_count || 0));

    const total = members.reduce((s, c) => s + (c.place_count || 0), 0);

    acc[slug] = { total, allCountries: members, name: regionDef.name };
    return acc;
  }, {});

  const toggleRegion = (slug, e) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedRegion(prev => prev === slug ? null : slug);
  };

  return (
    <section className="py-6 sm:py-8 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[...REGION_ORDER]
            .filter(slug => regionStats[slug])
            .sort((a, b) => {
              const diff = (regionStats[b].total || 0) - (regionStats[a].total || 0);
              if (diff !== 0) return diff;
              return regionStats[a].name.localeCompare(regionStats[b].name);
            })
            .map(slug => {
            const stats = regionStats[slug];
            if (!stats) return null;
            const isEmpty = stats.total === 0;
            const isExpanded = expandedRegion === slug;

            if (isEmpty) {
              return (
                <div
                  key={slug}
                  className="bg-card border-2 border-border rounded-xl p-5 opacity-50 cursor-not-allowed"
                  aria-disabled="true"
                >
                  <h3 className="text-base font-heading font-bold mb-1 text-muted-foreground">{stats.name}</h3>
                  <p className="text-xs text-muted-foreground">No data yet</p>
                </div>
              );
            }

            return (
              <div
                key={slug}
                className={`bg-card border-2 rounded-xl transition-all ${isExpanded ? 'border-primary shadow-lg' : 'border-border hover:border-primary hover:shadow-lg'}`}
                data-testid={`region-card-${slug}`}
              >
                {/* Card header: name/count links to region page; chevron toggles country list */}
                <div className="p-5 flex items-start justify-between gap-2">
                  <Link
                    to={`/map/region/${slug}`}
                    className="flex-1 group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded"
                  >
                    <h3 className="text-base font-heading font-bold mb-1 text-foreground group-hover:text-primary transition-colors">
                      {stats.name}
                    </h3>
                    <p className="text-2xl font-bold text-primary">{stats.total.toLocaleString()}</p>
                  </Link>
                  <button
                    onClick={(e) => toggleRegion(slug, e)}
                    aria-expanded={isExpanded}
                    aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${stats.name} countries`}
                    className="mt-1 p-1 rounded hover:bg-muted transition-colors text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                      strokeWidth={2}
                    />
                  </button>
                </div>

                {/* Collapsible country list with place counts */}
                <div
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                  style={isExpanded ? { maxHeight: '28rem', opacity: 1 } : { maxHeight: 0, opacity: 0 }}
                  aria-hidden={!isExpanded}
                >
                  <div className="border-t border-border px-3 py-3 overflow-y-auto" style={{ maxHeight: '24rem' }}>
                    {stats.allCountries.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">No countries found</p>
                    ) : (
                      <div className="space-y-1">
                        {stats.allCountries.map(c => (
                          <Link
                            key={c.country_code}
                            to={`/map/${c.country_code.toLowerCase()}`}
                            onClick={e => e.stopPropagation()}
                            className="flex items-center justify-between py-1 px-1.5 rounded hover:bg-muted group/country transition-colors"
                          >
                            <span className="text-xs text-foreground group-hover/country:text-primary transition-colors truncate">
                              {c.name || c.country_code}
                            </span>
                            <span className="text-xs font-semibold text-primary ml-2 shrink-0">
                              {(c.place_count || 0).toLocaleString()}
                            </span>
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const { iso2, mapPath, name: mapCountryName } = useInferredCountry();

  // Change 4: Preload /api/businesses for inferred country on mount (fire-and-forget)
  useEffect(() => {
    if (!iso2) return;
    const controller = new AbortController();
    fetch(`${API}/businesses?country_code=${iso2.toUpperCase()}&limit=100`, { signal: controller.signal }).catch(() => {});
    return () => controller.abort();
  }, [iso2]);

  const cardHeading = mapCountryName
    ? `Find Businesses in ${mapCountryName}`
    : 'Find Bitcoin Businesses';

  return (
    <div className="min-h-screen bg-background relative">
      <PageMeta
        title="Global directory of Bitcoin-accepting businesses & meetups"
        description="Find Bitcoin-accepting merchants, meetup groups, and Bitcoin Walks near you. A global directory maintained by the Australian Bitcoin Industry Body."
        path="/"
      />
      {/* Hero Section */}
      <section className="relative py-6 sm:py-8 md:py-12 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight font-bold font-heading text-foreground mb-4 sm:mb-6" data-testid="hero-heading">
              Spend Bitcoin Globally
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-muted-foreground mb-8 sm:mb-12 leading-relaxed px-4" data-testid="hero-subheading">
              Find bitcoin merchants near you and across the globe.
            </p>

            {/* Stats Counter */}
            <div className="mb-4 sm:mb-6">
              <StatsCounter />
            </div>
          </div>
        </div>
      </section>

      {/* Global Approach Section */}
      <GlobalApproachSection />

      {/* Cards Section */}
      <section className="py-4 sm:py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Mobile Order: Find Businesses first */}
          <div className="flex flex-col md:flex-col-reverse gap-4 sm:gap-6">
            {/* Hero Card - Find Businesses (Full Width) */}
            <Link
              to={mapPath}
              className="bg-primary border-2 border-primary rounded-xl p-6 sm:p-8 md:p-10 hover:bg-primary-hover hover:shadow-xl transition-all cursor-pointer group relative overflow-hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 block"
              data-testid="find-businesses-card"
            >
              <div className="absolute top-0 right-0 w-32 sm:w-40 h-32 sm:h-40 bg-white/10 rounded-full -translate-x-8 -translate-y-8"></div>
              <div className="absolute bottom-0 left-0 w-20 sm:w-24 h-20 sm:h-24 bg-white/5 rounded-full translate-x-4 translate-y-4"></div>
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 relative z-10">
                <div className="flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
                  <Map className="w-12 sm:w-16 h-12 sm:h-16 text-white group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                  <div className="text-center sm:text-left">
                    <h3 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold mb-1 sm:mb-2 text-white">{cardHeading}</h3>
                    <p className="text-sm sm:text-base md:text-lg text-white/80">Explore Bitcoin-accepting businesses on the interactive map</p>
                  </div>
                </div>
                <div className="bg-white/20 hover:bg-white/30 rounded-lg px-5 sm:px-6 py-2.5 sm:py-3 text-white font-semibold transition-colors text-sm sm:text-base">
                  Open Map →
                </div>
              </div>
            </Link>

            {/* CTA Row — two equal cards side by side */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {/* List Your Business */}
              <Link
                to="/submit"
                className="bg-card border-2 border-border rounded-xl p-5 sm:p-6 hover:border-primary hover:shadow-lg transition-all group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 flex flex-col"
                data-testid="list-business-card"
              >
                <PlusCircle className="w-8 sm:w-10 h-8 sm:h-10 text-primary mb-3 sm:mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="text-lg sm:text-xl font-heading font-bold mb-2 text-foreground group-hover:text-primary transition-colors">List Your Business</h3>
                <p className="text-sm text-muted-foreground flex-1">Add your business to the directory — takes a few minutes.</p>
              </Link>

              {/* How Do I Accept Bitcoin? */}
              <Link
                to="/how-to-accept"
                className="bg-card border-2 border-border rounded-xl p-5 sm:p-6 hover:border-primary hover:shadow-lg transition-all cursor-pointer group flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
                data-testid="how-to-accept-card"
              >
                <HelpCircle className="w-8 sm:w-10 h-8 sm:h-10 text-primary mb-3 sm:mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="text-lg sm:text-xl font-heading font-bold mb-2 text-foreground group-hover:text-primary transition-colors">How Do I Accept Bitcoin?</h3>
                <p className="text-sm text-muted-foreground flex-1">Learn about payment solutions</p>
              </Link>
            </div>
          </div>

          {/* Browse Directory Link */}
          <div className="text-center mt-8">
            <button
              onClick={() => navigate('/directory')}
              className="text-primary hover:underline font-medium inline-flex items-center gap-2"
              data-testid="browse-directory-link"
            >
              Or browse the full directory →
            </button>
          </div>
        </div>
      </section>

    </div>
  );
};

export default Home;
